import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { userService } from '../../../services/userService';
import styles from '../Settings.module.css';

interface AccountTabProps {
  onNotification: (type: 'success' | 'error', message: string) => void;
}

const AccountTab: React.FC<AccountTabProps> = ({ onNotification }) => {
  const { user, logout } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleLogout = () => {
    logout();
    onNotification('success', 'Logged out successfully');
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.')) {
      return;
    }

    // Double confirmation for safety
    if (!window.confirm('This is your final warning. Your account and all associated data will be permanently deleted. Are you absolutely sure?')) {
      return;
    }

    setIsDeleting(true);
    
    try {
      await userService.deleteAccount();
      onNotification('success', 'Account deleted successfully');
      
      // Logout and redirect after a short delay
      setTimeout(() => {
        logout();
        // Redirect to home page
        window.location.href = '/';
      }, 2000);
      
    } catch (error: any) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete account. Please try again.';
      onNotification('error', errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <h3>Account Management</h3>
        <p className={styles.sectionDescription}>
          Manage your account settings, profile information, and security preferences.
        </p>
      </div>

      <div className={styles.accountContent}>
        {/* Profile Information Section */}
        <div className={styles.accountSection}>
          <h4>Profile Information</h4>
          <div className={styles.accountInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>First Name:</span>
              <span className={styles.infoValue}>{user?.first_name || 'Not set'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Last Name:</span>
              <span className={styles.infoValue}>{user?.last_name || 'Not set'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Email:</span>
              <span className={styles.infoValue}>{user?.email}</span>
            </div>
          </div>
          <button className={styles.editButton}>
            Edit Profile
          </button>
        </div>

        {/* Security Section */}
        <div className={styles.accountSection}>
          <h4>Security & Authentication</h4>
          <div className={styles.accountInfo}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Password:</span>
              <span className={styles.infoValue}>••••••••</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Two-Factor Authentication:</span>
              <span className={styles.infoValue}>Disabled</span>
            </div>
          </div>
          <div className={styles.securityActions}>
            <button className={styles.editButton}>
              Change Password
            </button>
            <button className={styles.editButton}>
              Enable 2FA
            </button>
          </div>
        </div>

        {/* Account Actions Section */}
        <div className={styles.accountSection}>
          <h4>Account Actions</h4>
          <div className={styles.accountActions}>
            <button
              onClick={handleLogout}
              className={styles.logoutButton}
            >
              Logout
            </button>
            <button
              onClick={handleDeleteAccount}
              className={styles.deleteButton}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting Account...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountTab;
